import type { BuyerActionsResponse } from '../types';

import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodBuyerActionsResponseSchema } from './validations';

/**
 * Fetches the available buyer actions for a call-off.
 * Returns the list of actions the buyer can perform based on call-off status.
 *
 * @param callOffId - The UUID of the call-off to fetch actions for.
 * @param options - Additional request options.
 * @returns The list of available buyer actions.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Off-Magic-Link/operation/get_available_buyer_actions_calloffs__calloff_id__magic_link_actions_get
 */
export const fetchBuyerActions = async (
  callOffId: UUID,
  options?: { signal?: AbortSignal },
): Promise<BuyerActionsResponse> => {
  const response = await vestigasApi
    .get(ENDPOINT.CALL_OFF.GET_BUYER_ACTIONS(callOffId), {
      signal: options?.signal,
    })
    .json<BuyerActionsResponse>();

  // Validate the response with Zod
  const validatedResponse = zodBuyerActionsResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Buyer Actions API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
